import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { PortableText } from '@portabletext/react';
import { fetchArticleBySlug, urlFor } from '../lib/sanity';
import { ArrowLeft } from 'lucide-react';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

const components = {
  types: {
    image: ({ value }) => {
      const src = value?.asset?.url || urlFor(value)?.width(1400).url();
      if (!src) return null;
      return (
        <figure className="my-10">
          <img src={src} alt={value?.alt || ''} className="w-full border border-black/10" />
          {value?.caption && (
            <figcaption className="font-mono text-[10px] tracking-widest uppercase text-ink-muted mt-3">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
  block: {
    h1: ({ children }) => (
      <h1 className="font-display font-bold tracking-tighter text-4xl md:text-5xl text-ink mt-12 mb-4">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="font-display font-bold tracking-tighter text-3xl md:text-4xl text-ink mt-10 mb-3">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="font-display font-semibold tracking-tight text-2xl text-ink mt-8 mb-2">{children}</h3>
    ),
    normal: ({ children }) => (
      <p className="text-lg text-ink leading-[1.75] mb-6">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-ember pl-6 my-8 text-xl italic text-ink-muted">
        {children}
      </blockquote>
    ),
  },
  marks: {
    link: ({ children, value }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer" className="underline decoration-ember underline-offset-4 hover:text-ember">
        {children}
      </a>
    ),
    strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="font-mono text-sm bg-cream-alt px-1.5 py-0.5 border border-black/10">{children}</code>
    ),
  },
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchArticleBySlug(slug)
      .then((data) => {
        if (!mounted) return;
        if (!data) setNotFound(true);
        else setArticle(data);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="article-loading" className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center font-mono text-xs tracking-widest uppercase text-ink-muted">
        Loading…
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div data-testid="article-not-found" className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center">
        <h1 className="font-display text-4xl text-ink mb-4">Article not found</h1>
        <Link to="/articles" className="text-ember underline underline-offset-4">Back to articles</Link>
      </div>
    );
  }

  return (
    <div data-testid="article-detail-page">
      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 pb-24">
        <Link
          to="/articles"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase text-ink-muted hover:text-ink transition-colors mb-10"
        >
          <ArrowLeft size={14} /> All articles
        </Link>

        <div className="flex items-center gap-3 mb-6">
          {article.category && (
            <span className="font-mono text-[10px] tracking-widest uppercase text-ember">
              {article.category}
            </span>
          )}
          <span className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
            {formatDate(article.publishedAt)}
          </span>
          {article.author?.name && (
            <span className="font-mono text-[10px] tracking-widest uppercase text-ink-muted">
              · {article.author.name}
            </span>
          )}
        </div>

        <h1 className="font-display font-bold tracking-tighter text-4xl md:text-6xl text-ink leading-[1.02] mb-8">
          {article.title}
        </h1>

        {article.excerpt && (
          <p className="text-xl text-ink-muted leading-relaxed mb-10">{article.excerpt}</p>
        )}

        {article.coverImageUrl && (
          <img
            src={article.coverImageUrl}
            alt={article.title}
            className="w-full border border-black/10 mb-12"
          />
        )}

        <div className="prose-content">
          {article.body && <PortableText value={article.body} components={components} />}
        </div>
      </article>
    </div>
  );
}
